import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { METRICS, StatCard, type StatCardProps } from '../../../shared/ui/StatCard';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  useAccountStore,
  ACCOUNT_PLATFORMS,
  platformLabel,
  type PlatformFilter,
} from '../../../entities/account';
import { RefreshCw } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { fetchOverview, refreshOverview, type OverviewLatest } from '../../../features/statistics/api/overview';
import { useCachedQuery, writeQuery } from '../../../shared/lib/queryCache';
import { formatCompactNumber } from '../../../features/statistics/lib/format-number';
import { RefreshingHint } from '../../../shared/ui/RefreshingHint';
import { useTikTokGeneralStats } from '../../../features/statistics/hooks/useTikTokGeneralStats';
import { resolveDateRange } from '../../../features/statistics/lib/date-range';
import { selectOverviewTotals, sumTikTokTotals } from '../lib/overviewTotals';
import { useRemembered } from '../../../shared/lib/remembered';

type Period = 'month' | 'rolling30';

const PERIODS: { id: Period; label: string; hint: string }[] = [
  { id: 'month', label: 'Этот месяц', hint: 'С 1-го числа по сегодня — как в отчётности за месяц' },
  { id: 'rolling30', label: '30 дней', hint: 'Скользящее окно: последние 30 дней, включая прошлый месяц' },
];

/** «1 – 16 сен. 2026» — точные границы, чтобы два периода нельзя было спутать. */
function formatRange(from: Date, to: Date): string {
  const day = (d: Date) => d.getDate();
  const monthYear = (d: Date) =>
    d.toLocaleDateString('ru-RU', { month: 'short', year: 'numeric' });
  return monthYear(from) === monthYear(to)
    ? `${day(from)}–${day(to)} ${monthYear(to)}`
    : `${day(from)} ${monthYear(from)} – ${day(to)} ${monthYear(to)}`;
}

const nf = (v: number | null | undefined) => (v ?? 0).toLocaleString('ru-RU');
const cf = (v: number | null | undefined) => formatCompactNumber(v ?? 0);

export function AccountsOverview() {
  const { accounts } = useAccountStore();
  // Плитки считает почасовой крон только за «этот месяц» и «30 дней» —
  // общий период раздела сюда не подходит, поэтому выбор свой, но помнится.
  const [savedPeriod, setPeriod] = useRemembered<Period>('stats:overview-period', 'month');
  const period: Period = PERIODS.some((p) => p.id === savedPeriod) ? savedPeriod : 'month';
  const [platform, setPlatform] = useState<PlatformFilter>('all');
  // Цифры за период меняются раз в час (их считает крон), а между вкладками
  // ходят постоянно. Поэтому показываем то, что уже приходило, и обновляем
  // молча — пустой экран на каждый заход тут ничем не оправдан.
  const overviewKey = `overview:${period}`;
  const {
    data: latest,
    error: loadError,
    isLoading: loading,
    isRefreshing,
    updatedAt,
  } = useCachedQuery<OverviewLatest | null>(
    overviewKey,
    () => fetchOverview(period).then((r) => r.latest),
  );

  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const error = refreshError ?? (loadError ? String(loadError) : null);

  const refresh = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const { latest } = await refreshOverview(period); // живой пересчёт из Instagram
      writeQuery(overviewKey, latest);
    } catch (e) {
      setRefreshError(String(e));
    } finally {
      setRefreshing(false);
    }
  };

  // TikTok в сводку идёт живым запросом: снапшотов в Postgres для него пока нет
  // (Instagram-цифры собирает почасовой крон). Аккаунтов ≤10 — по запросу терпимо.
  const ttRange = useMemo(() => {
    const now = new Date();
    return period === 'month'
      ? resolveDateRange({ type: 'month', year: now.getFullYear(), month: now.getMonth() + 1 })
      : resolveDateRange({ type: 'preset', id: 'last30' });
  }, [period]);
  const periodLabel = useMemo(() => formatRange(ttRange.from, ttRange.to), [ttRange]);
  const { rows: ttRows } = useTikTokGeneralStats(ttRange);
  const tt = useMemo(() => sumTikTokTotals(ttRows), [ttRows]);

  const totals = useMemo(
    () =>
      selectOverviewTotals({
        latest,
        tiktok: tt,
        instagramAccounts: accounts.length,
        filter: platform,
      }),
    [latest, tt, accounts.length, platform],
  );

  // ponytail: крупные числа показываем компактно (235,9M), точное — в title при наведении
  const statCards: StatCardProps[] = [
    // Стор аккаунтов грузится отдельно от сводки — пока он пуст, «из 0 всего» врёт, поэтому молчим
    { title: 'Активных аккаунтов', value: nf(totals.activeAccounts), subtitle: totals.totalAccounts && totals.activeAccounts <= totals.totalAccounts
        ? `из ${totals.totalAccounts} всего`
        : undefined, ...METRICS.accounts },
    // Охват — метрика Instagram: TikTok Display API её не отдаёт вовсе.
    { title: 'Общий охват', value: totals.reach == null ? '—' : cf(totals.reach), full: totals.reach == null ? undefined : nf(totals.reach), subtitle: totals.reach == null ? 'нет у TikTok' : platform === 'all' && tt.accounts > 0 ? 'только Instagram' : undefined, ...METRICS.reach },
    { title: 'Просмотры', value: cf(totals.views), full: nf(totals.views), ...METRICS.views },
    { title: 'Взаимодействия', value: cf(totals.interactions), full: nf(totals.interactions), ...METRICS.interactions },
    { title: 'Подписчики', value: cf(totals.followers), full: nf(totals.followers), ...METRICS.followers },
    { title: 'Средний ER', value: totals.er == null ? '—' : `${totals.er.toFixed(2)}%`, subtitle: totals.erNote, ...METRICS.er },
  ];

  // Топ по охвату есть только у Instagram; для TikTok строим топ по просмотрам.
  const chartData = useMemo(() => {
    if (platform === 'tiktok') {
      return ttRows
        .map((r) => ({ name: r.username, reach: 0, views: r.impressions ?? 0 }))
        .sort((a, b) => b.views - a.views)
        .slice(0, 10);
    }
    return (latest?.top_accounts ?? []).map((r) => ({ name: `@${r.username}`, reach: r.reach, views: r.views }));
  }, [latest, platform, ttRows]);
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Общая статистика</h2>
          <p className="text-muted-foreground">
            {platform === 'all' ? 'Сводка по всем аккаунтам' : `Только ${platformLabel(platform)}`}
            {' · '}
            {periodLabel}
            {/* Цифры могут быть из кэша — говорим прямо, на какой они момент,
                иначе вчерашние легко принять за живые. Пока идёт фоновое
                обновление, на этом же месте написано «Обновляем…». */}
            {(updatedAt || isRefreshing) && ' · '}
            {isRefreshing ? (
              <RefreshingHint active />
            ) : (
              updatedAt &&
              `данные на ${new Date(updatedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Платформы берутся из реестра: новая сеть = +1 кнопка, без правок здесь */}
          <div className="inline-flex overflow-hidden rounded-lg border p-0.5">
            {([{ id: 'all' as const, label: 'Все' }, ...ACCOUNT_PLATFORMS]).map(({ id, label }) => (
              <button
                key={id}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  platform === id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setPlatform(id)}
              >{label}</button>
            ))}
          </div>
          <div className="inline-flex overflow-hidden rounded-lg border p-0.5">
            {PERIODS.map(({ id, label, hint }) => (
              <button
                key={id}
                title={hint}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  period === id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setPeriod(id)}
              >{label}</button>
            ))}
          </div>
          {/* Кнопка пересчитывает Instagram-агрегат из крона; TikTok и так живой.
              Не убираем её из разметки на TikTok — иначе панель схлопывается и
              переключатели уезжают вправо прямо под курсором. */}
          <Button
            onClick={refresh}
            disabled={refreshing || loading || platform === 'tiktok'}
            variant="outline"
            size="sm"
            title={platform === 'tiktok'
              ? 'Статистика TikTok приходит вживую — пересчитывать нечего'
              : undefined}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Обновление…' : 'Обновить'}
          </Button>
        </div>
      </div>

      {/* Ошибка и «нет данных» относятся к Instagram-агрегату из крона */}
      {platform !== 'tiktok' && error && <div className="mb-4 text-sm text-red-600">{error}</div>}
      {platform !== 'tiktok' && !latest && !loading && (
        <Badge variant="outline" className="mb-4">Данные ещё не собраны — почасовой крон наполнит таблицу</Badge>
      )}

      {/* 6 карточек → сетка по 3, без «сиротской» строки как при lg:grid-cols-4 */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {statCards.map((stat, i) => (
          <StatCard key={i} {...stat} loading={platform !== 'tiktok' && loading && !latest} />
        ))}
      </div>

      <div className="mt-8">
        <Card>
          <CardHeader>
            {/* У TikTok охвата нет — там топ строится по просмотрам */}
            <CardTitle>
              {totals.showReachChart ? 'Топ аккаунтов по охвату' : 'Топ аккаунтов по просмотрам'}
            </CardTitle>
            <CardDescription>
              {totals.showReachChart
                ? `10 аккаунтов с наибольшим охватом${platform === 'all' && tt.accounts > 0 ? ' · только Instagram' : ''}`
                : '10 аккаунтов с наибольшим числом просмотров'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length === 0 ? (
              <div className="flex h-[360px] items-center justify-center text-muted-foreground">
                Нет данных
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 70 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-muted" vertical={false} />
                  <XAxis
                    dataKey="name" angle={-45} textAnchor="end" interval={0} height={80}
                    tickLine={false} axisLine={false} tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    tickFormatter={formatCompactNumber}
                    tickLine={false} axisLine={false} width={56} tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    formatter={(v: number) => nf(v)}
                    cursor={{ fill: 'currentColor', fillOpacity: 0.06 }}
                    contentStyle={{ borderRadius: 12, border: '1px solid var(--border)', background: 'var(--card)' }}
                  />
                  <Legend verticalAlign="top" align="right" iconType="circle" iconSize={8} />
                  {/* Колонку охвата рисуем только там, где метрика есть */}
                  {totals.showReachChart && (
                    <Bar dataKey="reach" fill="var(--chart-1)" name="Охват" radius={[4, 4, 0, 0]} />
                  )}
                  <Bar dataKey="views" fill="var(--chart-2)" name="Просмотры" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
